import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Box, Typography, Button, Paper } from '@mui/material';
import { Block as BlockIcon } from '@mui/icons-material';

// Loading component while checking auth
const AuthLoading = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      bgcolor: 'background.default',
    }}
  >
    <Typography variant="h6" color="text.secondary">
      Loading...
    </Typography>
  </Box>
);

// Access denied component
const AccessDenied = ({ requiredPermissions }) => {
  const { logout } = useAuth();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center' }}>
        <BlockIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          You don't have permission to access this page. Please contact your administrator
          if you believe this is an error.
        </Typography>
        {requiredPermissions && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Required permissions: {requiredPermissions.join(', ')}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button variant="outlined" onClick={() => window.history.back()}>
            Go Back
          </Button>
          <Button variant="contained" color="error" onClick={logout}>
            Logout
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

// Protected route component
const ProtectedRoute = ({
  children,
  permissions = [],
  requireAll = false,
  adminOnly = false,
}) => {
  const { isAuthenticated, loading, hasPermission, hasAnyPermission, hasAllPermissions, isAdmin } = useAuth();
  const location = useLocation();

  // Still loading auth state
  if (loading) {
    return <AuthLoading />;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check admin requirement
  if (adminOnly && !isAdmin()) {
    return <AccessDenied requiredPermissions={['admin']} />;
  }

  // Check permissions
  if (permissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasAccess) {
      return <AccessDenied requiredPermissions={permissions} />;
    }
  }

  return children;
};

// Public route - only accessible when NOT logged in
export const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <AuthLoading />;
  }

  if (isAuthenticated) {
    // Redirect to the page they tried to visit or dashboard
    const from = location.state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  return children;
};

export default ProtectedRoute;
